/* eslint-disable replo/no-tailwind-literal-color */

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useReactRenderStore } from "../store/reactRenderStore";
import { findPageFileFromPath } from "../utils/filesUtils";
import ReactRender from "./ReactRender";

function ReactInlineEditor() {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const { pageId } = useParams();
  const { selectedElement, setSelectedElement, editText, setEditText } =
    useReactRenderStore();

  const { data: files = [], refetch: refetchFiles } = trpc.file.list.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const { data: page } = trpc.file.find.useQuery(
    projectId ? { projectId, fileId: pageId ?? "" } : skipToken,
    {
      enabled: Boolean(projectId && pageId),
    },
  );

  const { mutate: updateText, isPending } =
    trpc.file.updateElementText.useMutation({
      onSuccess: () => {
        void refetchFiles();
      },
    });

  const handleEditTextSubmit = () => {
    if (selectedElement && page) {
      const openFile = findPageFileFromPath(page.path, files);

      if (openFile) {
        updateText({
          projectId: projectId ?? "",
          openFile,
          updatedText: editText ?? "",
          userId: user?.id ?? "",
          selectedElement: {
            id: selectedElement.id,
            className: selectedElement.class,
            text: selectedElement.text,
            tagName: selectedElement.tagName,
          },
        });
      }
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg border border-gray-200">
      <ReactRender />
      {selectedElement && (
        <div className="fixed top-0 right-0 z-50 h-full w-[300px] overflow-y-auto border-l border-gray-200 bg-white shadow-lg">
          <div className="p-4 pt-20">
            <h3 className="mb-4 border-b pb-2 text-lg font-medium">
              Selected Element
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Tag</span>
                <p className="mt-1">{selectedElement.tagName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Class</span>
                <p className="mt-1 break-all">{selectedElement.class}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">ID</span>
                <p className="mt-1">{selectedElement.id || "none"}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Text</span>
                <Textarea
                  value={editText ?? ""}
                  onChange={(e) => setEditText(e)}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  isLoading={isPending}
                  variant="primary"
                  onClick={handleEditTextSubmit}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setSelectedElement(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReactInlineEditor;
